import { api } from './client'

export interface FormField {
  key: string
  label: string
  type: string
  required?: boolean
  /** For type "select" — the choices offered on the public form. */
  options?: string[]
  /**
   * Target mapping (Forms v2): which entity the answer is written to and whether it is a custom
   * field. A system contact slot has {entity:'contact', custom:false, key: first_name|last_name|
   * email|phone|mobile}; a custom field has custom:true and key = the field-definition key.
   * Absent / unmapped answers are only captured in the submission record.
   */
  entity?: 'contact' | 'deal'
  custom?: boolean
  /** Not shown to visitors; every submission saves {@link value} here (e.g. Type of enquiry = Buying). */
  hidden?: boolean
  value?: string
}

export interface Form {
  id: string
  name: string
  enabled: boolean
  fields: FormField[]
  createDeal: boolean
  dealPipelineId: string | null
  description?: string | null
  thankYouMessage?: string | null
  redirectUrl?: string | null
  /** Owner assigned to the created contact (and deal). */
  ownerUserId?: string | null
  /** Stage the created deal lands in; null = the pipeline's first stage. */
  dealStageId?: string | null
  /** Deal title, rendered from {{field_key}} tokens; null = default "{First} {Last} — web lead". */
  dealNameTemplate?: string | null
  /** Email addresses notified when the form is submitted. */
  notifyEmails?: string[]
  /** Hide the form's own title on the public page (the embedding page usually has a heading). */
  hideTitle?: boolean
  style?: FormStyle
  /** Submit button text; null = "Submit". */
  submitLabel?: string | null
  /** After submit: add the submitter to this manual list / email them this template / enrol them. */
  addToListId?: string | null
  autoReplyTemplateId?: string | null
  enrolSequenceId?: string | null
  /** The listing (deal) this is the enquiry form for — an off-market campaign. */
  listingDealId?: string | null
  /** Leads captured so far, and when the last came in. */
  submissionCount?: number
  lastSubmissionAt?: string | null
  updatedAt?: string
}

/** Colours of the public form (hex); a missing slot uses the agency's brand. */
export interface FormStyle {
  background?: string
  text?: string
  accent?: string
  buttonText?: string
}

export interface FormInput {
  name: string
  enabled?: boolean
  fields: FormField[]
  createDeal?: boolean
  dealPipelineId?: string | null
  description?: string | null
  thankYouMessage?: string | null
  redirectUrl?: string | null
  ownerUserId?: string | null
  dealStageId?: string | null
  dealNameTemplate?: string | null
  notifyEmails?: string[]
  /** Hide the form's own title on the public page (the embedding page usually has a heading). */
  hideTitle?: boolean
  /** Omit to keep the current colours; {} resets them to the brand. */
  style?: FormStyle
  submitLabel?: string | null
  addToListId?: string | null
  autoReplyTemplateId?: string | null
  enrolSequenceId?: string | null
  listingDealId?: string | null
}

export interface FormSubmission {
  id: string
  formId: string
  data: Record<string, unknown>
  contactId: string | null
  dealId: string | null
  createdAt: string
}

export interface PublicForm {
  id: string
  name: string
  fields: FormField[]
  description?: string | null
  /** When set, the public page omits the form's own title (the embedding page has its heading). */
  hideTitle?: boolean
  style?: FormStyle
  submitLabel?: string | null
}

export async function listForms(dealId?: string): Promise<Form[]> {
  return (await api.get<Form[]>('/api/forms', { params: { dealId } })).data
}

export async function getForm(id: string): Promise<Form> {
  return (await api.get<Form>(`/api/forms/${id}`)).data
}

export async function createForm(input: FormInput): Promise<Form> {
  return (await api.post<Form>('/api/forms', input)).data
}

export async function updateForm(id: string, input: FormInput): Promise<Form> {
  return (await api.put<Form>(`/api/forms/${id}`, input)).data
}

export async function deleteForm(id: string): Promise<void> {
  await api.delete(`/api/forms/${id}`)
}

export async function listSubmissions(formId: string): Promise<FormSubmission[]> {
  return (await api.get<FormSubmission[]>(`/api/forms/${formId}/submissions`)).data
}

// --- public (no auth) ---

export async function getPublicForm(id: string): Promise<PublicForm> {
  return (await api.get<PublicForm>(`/api/forms/${id}/public`)).data
}

export interface SubmitResult {
  ok?: boolean
  submissionId?: string
  thankYouMessage?: string | null
  redirectUrl?: string | null
  hideTitle?: boolean
}

export async function submitPublicForm(id: string, data: Record<string, unknown>): Promise<SubmitResult> {
  return (await api.post<SubmitResult>(`/api/forms/${id}/submit`, data)).data
}
